import {
  TAIWAN_LANDLINE_EXTENSION_PRESET,
  TAIWAN_LANDLINE_EXTENSION_REGEX,
  TAIWAN_MOBILE_OR_LANDLINE_EXTENSION_PRESET,
  TAIWAN_MOBILE_OR_LANDLINE_EXTENSION_REGEX,
  TAIWAN_MOBILE_PRESET,
  TAIWAN_MOBILE_REGEX,
} from '@/lib/phone-validation'
import { isCompleteTaiwanAddressAnswer } from '@/lib/taiwan-address'
import { isLayoutQuestionType } from '@/types/form'
import type { AnswerValue, Question } from '@/types/form'

const EMAIL_REGEX = '^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$'
const GENERIC_PHONE_REGEX = '^[0-9+()\\-\\s]{6,}$'
const URL_REGEX = '^https?://.+\\..+'
const NUMBER_REGEX = '^-?\\d+(\\.\\d+)?$'
const INTEGER_REGEX = '^-?\\d+$'
const LEGACY_TAIWAN_LANDLINE_REGEX = '^0\\d{1,3}\\s?\\d{3,4}\\s?\\d{3,4}(?:#\\d+)?$'

type ValidationKind = 'email' | 'phone' | 'url' | 'number' | 'taiwan_mobile' | 'taiwan_landline'

type ValidationRule = {
  regex: string
  kind: ValidationKind
}

export function getQuestionValidationError(question: Question, value: AnswerValue | undefined) {
  if (isLayoutQuestionType(question.type)) return null

  const isEmpty = isEmptyAnswer(value)
  if (question.required && isEmpty) {
    if (Array.isArray(value)) return question.settings.errorMessage ?? '請至少選擇一項'
    return question.settings.errorMessage ?? '請完成此題'
  }
  if (isEmpty) return null

  if (question.type === 'TAIWAN_ADDRESS' && !isCompleteTaiwanAddressAnswer(value)) {
    return question.settings.errorMessage ?? '請完成此題'
  }

  const textValue = normalizeAnswerText(value)
  if (!textValue) return null

  if (
    typeof question.settings.minLength === 'number' &&
    textValue.length < question.settings.minLength
  ) {
    return question.settings.errorMessage ?? `請至少輸入 ${question.settings.minLength} 個字。`
  }
  if (
    typeof question.settings.maxLength === 'number' &&
    textValue.length > question.settings.maxLength
  ) {
    return question.settings.errorMessage ?? `請勿超過 ${question.settings.maxLength} 個字。`
  }

  const rule = getValidationRule(question)
  if (!rule) return null

  let regex: RegExp
  try {
    regex = new RegExp(rule.regex)
  } catch {
    return '這題的格式驗證設定有誤，請聯絡表單管理者。'
  }

  if (regex.test(textValue)) return null
  return question.settings.errorMessage ?? getFormatErrorMessage(rule.kind)
}

function isEmptyAnswer(value: AnswerValue | undefined) {
  if (value === undefined || value === null) return true
  if (typeof value === 'string') return value.trim() === ''
  if (Array.isArray(value)) return value.length === 0
  if (typeof value === 'object') {
    return !Object.values(value).some((item) => {
      if (item === undefined || item === null) return false
      if (typeof item === 'string') return item.trim() !== ''
      if (Array.isArray(item)) return item.length > 0
      return true
    })
  }
  return false
}

function normalizeAnswerText(value: AnswerValue | undefined) {
  if (typeof value === 'string') return value.trim()
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  if (Array.isArray(value)) return value.map(String).join('、').trim()
  if (value && typeof value === 'object') {
    return Object.values(value)
      .filter((item) => item !== undefined && item !== null && item !== '')
      .map(String)
      .join(' ')
      .trim()
  }
  return ''
}

function getValidationRule(question: Question): ValidationRule | null {
  const preset = String(question.settings.validationPreset ?? '')
  const validation = question.settings.validation
  const explicitRegex =
    typeof question.settings.validationRegex === 'string' &&
    question.settings.validationRegex.trim()
      ? question.settings.validationRegex
      : ''

  if (question.type === 'EMAIL') {
    return { regex: explicitRegex || EMAIL_REGEX, kind: 'email' }
  }
  if (question.type === 'WEBSITE') {
    return { regex: explicitRegex || URL_REGEX, kind: 'url' }
  }
  if (question.type === 'NUMBER') {
    return { regex: explicitRegex || NUMBER_REGEX, kind: 'number' }
  }
  if (question.type === 'TAIWAN_MOBILE') {
    return { regex: explicitRegex || TAIWAN_MOBILE_REGEX, kind: 'taiwan_mobile' }
  }
  if (question.type === 'TAIWAN_LANDLINE') {
    if (!explicitRegex || explicitRegex === LEGACY_TAIWAN_LANDLINE_REGEX || preset !== 'custom') {
      return { regex: TAIWAN_LANDLINE_EXTENSION_REGEX, kind: 'taiwan_landline' }
    }
    return { regex: explicitRegex, kind: 'taiwan_landline' }
  }

  if (preset === TAIWAN_MOBILE_PRESET) {
    return { regex: explicitRegex || TAIWAN_MOBILE_REGEX, kind: 'taiwan_mobile' }
  }
  if (preset === TAIWAN_LANDLINE_EXTENSION_PRESET) {
    return { regex: explicitRegex || TAIWAN_LANDLINE_EXTENSION_REGEX, kind: 'taiwan_landline' }
  }
  if (preset === TAIWAN_MOBILE_OR_LANDLINE_EXTENSION_PRESET) {
    return { regex: explicitRegex || TAIWAN_MOBILE_OR_LANDLINE_EXTENSION_REGEX, kind: 'phone' }
  }
  if (preset === 'integer') return { regex: explicitRegex || INTEGER_REGEX, kind: 'number' }
  if (preset === 'custom' && explicitRegex) {
    return { regex: explicitRegex, kind: validationToKind(validation) }
  }

  if (explicitRegex) return { regex: explicitRegex, kind: validationToKind(validation) }
  if (validation === 'email') return { regex: EMAIL_REGEX, kind: 'email' }
  if (validation === 'phone') return { regex: GENERIC_PHONE_REGEX, kind: 'phone' }
  if (validation === 'url') return { regex: URL_REGEX, kind: 'url' }
  if (validation === 'number') return { regex: NUMBER_REGEX, kind: 'number' }

  return null
}

function validationToKind(validation: Question['settings']['validation']): ValidationKind {
  if (validation === 'email') return 'email'
  if (validation === 'url') return 'url'
  if (validation === 'number') return 'number'
  return 'phone'
}

function getFormatErrorMessage(kind: ValidationKind) {
  if (kind === 'email') return '請輸入有效的 Email 格式。'
  if (kind === 'url') return '請輸入有效的網址格式，需以 http:// 或 https:// 開頭。'
  if (kind === 'number') return '請輸入有效的數字格式。'
  if (kind === 'taiwan_mobile')
    return '請輸入正確的台灣手機格式，例如 0912345678 或 +886912345678。'
  if (kind === 'taiwan_landline')
    return '請輸入正確的台灣市話格式，可加分機，例如 02-26589848#123。'
  return '請輸入有效的電話格式。'
}
